import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import Navbar from "../../components/layout/Navbar";
import { AuthContext } from "../../context/AuthContext";
import { getJobActivities, saveJobActivities } from "../../services/jobActivities.service";
import { 
  ChevronLeft, 
  Save, 
  PlusCircle, 
  MinusCircle, 
  Clock, 
  CheckCircle2, 
  MapPin, 
  Camera, 
  PenTool, 
  FileUp,
  Info
} from "lucide-react";

/* Importación de componentes de comportamiento (Mantener lógica actual) */
import GeolocationInput from "../../components/behaviours/GeolocationInput";
import PhotoInput from "../../components/behaviours/PhotoInput";
import SignatureInput from "../../components/behaviours/SignatureInput";
import FileInput from "../../components/behaviours/FileInput";

const toLocalSqlString = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => (n < 10 ? "0" + n : n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export default function ExecuteJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [activities, setActivities] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchActivities = async () => {
      const token = localStorage.getItem("token") || "";
      const data = await getJobActivities(token, parseInt(id));
      setActivities(data);

      const initialValues = {};
      data.forEach((act, index) => {
        initialValues[index] = [{
          startDate: act.startDate ? new Date(act.startDate) : null,
          endDate: act.endDate ? new Date(act.endDate) : null,
          quantity: act.quantity || "",
          checked: act.behaviourTypeCode === "CHECK" && act.quantity === 1,
          text: act.behaviourTypeCode === "TEXT_INPUT" ? act.observation || "" : "",
          fileType: act.fileType || null,
          filePath: act.filePath || null,
          latitude: act.latitude || null,
          longitude: act.longitude || null,
          accuracy: act.accuracy || null,
          scannedCode: act.scannedCode || null,
        }];
      });
      setFormValues(initialValues);
      setLoading(false);
    };
    fetchActivities();
  }, [id]);

  const handleChange = (activityIndex, repeatIndex, field, value) => {
    setFormValues((prev) => {
      const current = [...prev[activityIndex]];
      current[repeatIndex] = { ...current[repeatIndex], [field]: value };
      return { ...prev, [activityIndex]: current };
    });
  };

  const handleAddRepeat = (activityIndex) => {
    setFormValues((prev) => ({
      ...prev,
      [activityIndex]: [...prev[activityIndex], {
        startDate: null, endDate: null, quantity: "", checked: false, text: "",
        fileType: null, filePath: null, latitude: null, longitude: null, accuracy: null, scannedCode: null,
      }],
    }));
  };

  const handleRemoveRepeat = (activityIndex, repeatIndex) => {
    if (repeatIndex === 0) return;
    setFormValues((prev) => {
      const current = [...prev[activityIndex]];
      current.splice(repeatIndex, 1);
      return { ...prev, [activityIndex]: current };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token") || "";
      const activitiesPayload = activities.flatMap((act, activityIndex) => {
        const repeats = formValues[activityIndex];
        return repeats.map((r) => ({
          DetailsTakeUnit: act.detailsTakeUnit || null,
          ActivityId: act.activityId,
          StartDate: r.startDate ? toLocalSqlString(r.startDate) : null,
          EndDate: r.endDate ? toLocalSqlString(r.endDate) : null,
          Quantity: act.behaviourTypeCode === "CHECK" ? (r.checked ? 1 : 0) : 
                    act.behaviourTypeCode === "NUMERIC_INPUT" ? parseFloat(r.quantity) || null : null,
          Observation: act.behaviourTypeCode === "TEXT_INPUT" ? r.text || null : null,
          FileType: r.fileType || null,
          FilePath: r.filePath || null,
          Latitude: r.latitude || null,
          Longitude: r.longitude || null,
          Accuracy: r.accuracy || null,
          ScannedCode: r.scannedCode || null,
          UserCreated: user?.dni || "system",
          Created: toLocalSqlString(new Date()),
          UserModified: user?.dni || "system",
          Modified: toLocalSqlString(new Date()),
        }));
      });

      const filtered = activitiesPayload.filter(a => a.StartDate || a.EndDate || a.Quantity !== null || a.Observation !== null || a.FilePath !== null);
      if (filtered.length === 0) {
        alert("⚠️ No hay datos válidos para guardar.");
        setSaving(false);
        return;
      }

      const result = await saveJobActivities(token, parseInt(id), filtered);
      if (result.success === 1) {
        alert("✅ Actividades guardadas correctamente");
        navigate("/jobs");
      }
    } catch (err) {
      alert("❌ Error al guardar actividades");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <p className="text-slate-500 font-bold animate-pulse tracking-widest">SINCRONIZANDO ACTIVIDADES...</p>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden text-slate-900">
      <Sidebar />

      <main className="flex-1 flex flex-col overflow-hidden">
        <Navbar />

        {/* TOP BAR / BREADCRUMBS */}
        <div className="px-8 py-5 bg-white border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-900"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div>
              <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em]">Ejecución en vivo</span>
              <h2 className="text-2xl font-black text-slate-900 leading-none">Orden de Trabajo #{id}</h2>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-2 rounded-xl border border-emerald-100">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wider">Modo Formulario Activo</span>
          </div>
        </div>

        {/* LISTADO DE ACTIVIDADES */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-8 scrollbar-thin scrollbar-thumb-slate-200">
          <div className="max-w-4xl mx-auto space-y-10 pb-20">
            
            {activities.map((act, index) => (
              <section key={act.activityId} className="relative">
                {/* Indicador de Línea de Tiempo */}
                <div className="absolute -left-6 top-0 bottom-0 w-[2px] bg-slate-200 hidden lg:block"></div>
                <div className="absolute -left-[30px] top-1 w-3 h-3 rounded-full bg-blue-500 ring-4 ring-blue-100 hidden lg:block"></div>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-slate-900 text-white text-[10px] font-black px-2 py-1 rounded">
                      {act.activityCode || `ACT-${index + 1}`}
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">{act.activityName}</h3>
                  </div>

                  {act.isRepeatable && (
                    <button
                      onClick={() => handleAddRepeat(index)}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-bold text-sm bg-blue-50 px-3 py-1.5 rounded-lg transition-colors border border-blue-100"
                    >
                      <PlusCircle className="w-4 h-4" /> Repetir
                    </button>
                  )}
                </div>

                {formValues[index]?.map((r, repeatIndex) => (
                  <div 
                    key={repeatIndex} 
                    className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow mb-4 relative group"
                  >
                    {repeatIndex > 0 && (
                      <button 
                        onClick={() => handleRemoveRepeat(index, repeatIndex)}
                        className="absolute -top-3 -right-3 bg-white text-red-500 shadow-lg rounded-full p-1 hover:bg-red-50 transition-colors border border-red-100"
                      >
                        <MinusCircle className="w-6 h-6" />
                      </button>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* COLUMNA 1: Tiempos */}
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest">
                            <Clock className="w-3 h-3" /> Inicio de Actividad
                          </label>
                          <input
                            type="datetime-local"
                            value={r.startDate ? toLocalSqlString(r.startDate).replace(" ", "T").slice(0, 16) : ""}
                            onChange={(e) => handleChange(index, repeatIndex, "startDate", e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                          />
                        </div>

                        {(act.behaviourTypeCode === "DURATION" || r.endDate) && (
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest">
                              <CheckCircle2 className="w-3 h-3 text-emerald-500" /> Fin de Actividad
                            </label>
                            <input
                              type="datetime-local"
                              value={r.endDate ? toLocalSqlString(r.endDate).replace(" ", "T").slice(0, 16) : ""}
                              onChange={(e) => handleChange(index, repeatIndex, "endDate", e.target.value)}
                              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all border-emerald-100"
                            />
                          </div>
                        )}
                      </div>

                      {/* COLUMNA 2: Inputs de Datos */}
                      <div className="bg-slate-50/50 p-6 rounded-2xl border border-dashed border-slate-200">
                        {act.behaviourTypeCode === "CHECK" && (
                          <label className="flex items-center gap-4 cursor-pointer group/check">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${r.checked ? 'bg-emerald-500 text-white shadow-lg' : 'bg-white border-2 border-slate-200 text-slate-300 group-hover/check:border-blue-400'}`}>
                              <CheckCircle2 className="w-6 h-6" />
                            </div>
                            <input
                              type="checkbox"
                              checked={r.checked}
                              onChange={(e) => handleChange(index, repeatIndex, "checked", e.target.checked)}
                              className="hidden"
                            />
                            <div>
                              <p className="font-bold text-slate-900 text-sm">Validar ejecución</p>
                              <p className="text-xs text-slate-500 italic">Marca para confirmar este paso</p>
                            </div>
                          </label>
                        )}

                        {act.behaviourTypeCode === "NUMERIC_INPUT" && (
                          <div className="space-y-2">
                             <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Medición Numérica</label>
                             <input
                              type="number"
                              value={r.quantity}
                              onChange={(e) => handleChange(index, repeatIndex, "quantity", e.target.value)}
                              placeholder="0.00"
                              className="text-2xl font-black w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 outline-none pb-2 transition-all placeholder:text-slate-200"
                            />
                          </div>
                        )}

                        {act.behaviourTypeCode === "TEXT_INPUT" && (
                          <textarea
                            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none resize-none text-sm italic"
                            rows={3}
                            value={r.text}
                            onChange={(e) => handleChange(index, repeatIndex, "text", e.target.value)}
                            placeholder="Añadir notas del operario o hallazgos encontrados..."
                          />
                        )}

                        {/* COMPONENTES DE EVIDENCIA */}
                        <div className="grid grid-cols-1 gap-4 mt-2">
                          {act.behaviourTypeCode === "SIGNATURE" && <SignatureInput value={r.filePath} onChange={(url) => { handleChange(index, repeatIndex, "filePath", url); handleChange(index, repeatIndex, "fileType", "image/png"); }} />}
                          {act.behaviourTypeCode === "PHOTO_INPUT" && <PhotoInput token={localStorage.getItem("token")} jobId={parseInt(id)} activityId={act.activityId} value={{ filePath: r.filePath, fileType: r.fileType }} onChange={(data) => { handleChange(index, repeatIndex, "filePath", data.filePath); handleChange(index, repeatIndex, "fileType", data.fileType); if(!r.startDate) handleChange(index, repeatIndex, "startDate", new Date()); }} />}
                          {act.behaviourTypeCode === "FILE_INPUT" && <FileInput value={{ filePath: r.filePath, fileType: r.fileType, fileName: r.fileName }} onChange={(info) => { handleChange(index, repeatIndex, "filePath", info.filePath); handleChange(index, repeatIndex, "fileType", info.fileType); handleChange(index, repeatIndex, "fileName", info.fileName); }} />}
                          {act.behaviourTypeCode === "GEOLOCATION" && <GeolocationInput value={{ latitude: r.latitude, longitude: r.longitude, accuracy: r.accuracy }} onChange={(geo) => { handleChange(index, repeatIndex, "latitude", geo.latitude); handleChange(index, repeatIndex, "longitude", geo.longitude); handleChange(index, repeatIndex, "accuracy", geo.accuracy); }} />}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>
        </div>

        {/* FOOTER DE ACCIÓN PERSISTENTE */}
        <footer className="bg-white border-t border-slate-200 px-8 py-5 flex items-center justify-between shadow-[0_-10px_30px_rgba(0,0,0,0.03)]">
          <div className="hidden md:flex items-center gap-3 text-slate-400 italic text-sm">
            <Info className="w-4 h-4" />
            Recuerda que todos los campos con (*) son obligatorios según el estándar.
          </div>
          <div className="flex gap-4 w-full md:w-auto">
            <button
              onClick={() => navigate("/jobs")}
              className="flex-1 md:flex-none px-6 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-all active:scale-95"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-10 py-3 rounded-xl font-black text-white transition-all shadow-xl active:scale-95 ${saving ? "bg-slate-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20"}`}
            >
              {saving ? <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div> : <Save className="w-5 h-5" />}
              {saving ? "Procesando..." : "Finalizar y Guardar"}
            </button>
          </div>
        </footer>
      </main>
    </div>
  );
}